<template>
  <SlideIn :open="isOpen" heading="Added to bag" @close="isOpen = false">
    <template #content>
      <CartLine
        v-for="line in lines"
        :key="line.id"
        :line="line"
        @removeLine="handleRemoveCartLine(line)"
        @updateLine="(val) => handleUpdateCartLine(line, val)"
      />

      <NostoProductRecommendationsGrid
        :recommendations="nostoProductRecommendationsProduct"
        :placement="nostoProductRecommendationsProductId"
        heading="You might also like"
      />
      <NostoProductRecommendationsGrid
        :recommendations="nostoProductRecommendationsBehaviour"
        :placement="nostoProductRecommendationsBehaviourId"
        heading="An edit just for you"
      />
    </template>
    <template #footer>
      <div class="button-group">
        <div class="cart-price">
          <span class="cart-price__heading">Subtotal</span>
          <span class="cart-price__value">
            {{ $filters.toCurrency(subtotal.amount, subtotal.currencyCode, 0) }}
          </span>
        </div>
        <DeliveryEstimatorNoInput
          v-if="isEDDEnabled"
          class="estimate-delivery-date"
        />
        <Button full-width theme="outline" @click="handleContinueShopping">
          Continue shopping
        </Button>
        <Button full-width @click="handleGoToBag"> Go to bag </Button>
      </div>
    </template>
  </SlideIn>
</template>

<script>
import { unwrapNodes } from "@/helpers/graphql";
import { mapState, mapActions, mapGetters } from "vuex";
import SlideIn from "@/components/SlideIn.vue";
import Button from "@/components/Button.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInput.vue";
import CartLine from "@/components/cart/CartLine.vue";
import NostoProductRecommendationsGrid from "@/components/NostoProductRecommendationsGrid.vue";
import { regionFeatureToggles } from "@/config.js";

export default {
  name: "AddedSetToBag",
  components: {
    Button,
    SlideIn,
    DeliveryEstimatorNoInput,
    CartLine,
    NostoProductRecommendationsGrid,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    variants: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:open"],
  data() {
    return {
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviourId: null,
    };
  },
  computed: {
    ...mapState("cart", ["cart"]),
    ...mapGetters("app", ["currencyCode"]),
    isOpen: {
      get() {
        return this.open;
      },
      set(value) {
        return this.$emit("update:open", value);
      },
    },
    subtotal() {
      return this.cart.estimatedCost.subtotalAmount;
    },
    lines() {
      const lines = unwrapNodes(this.cart?.lines.edges) || [];

      return lines.filter((line) => {
        return Object.values(this.variants).some((variantId) => {
          return line.merchandise.id === variantId;
        });
      });
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.sendNosto();
      }
    },
  },
  methods: {
    ...mapActions("cart", [
      "removeCartItem",
      "updateCartItem",
      "setCartOpen",
      "checkCartRules",
    ]),
    handleGoToBag() {
      this.isOpen = false;
      this.setCartOpen(true);
    },
    async handleRemoveCartLine({ id }) {
      try {
        await this.removeCartItem(id);
      } catch (error) {
        console.log(error);
      }

      if (this.lines.length === 0) {
        this.isOpen = false;
      }
    },
    async handleUpdateCartLine(line, newQuantity) {
      if (newQuantity > line.quantity) {
        const cartRuleResults = await this.checkCartRules(
          line.merchandise.product.id
        );
        if (!cartRuleResults) {
          return;
        }
      }

      try {
        await this.updateCartItem({
          id: line.id,
          quantity: newQuantity,
          merchandiseId: line.merchandise.id,
        });
      } catch (error) {
        console.log(error);
      }
    },
    handleContinueShopping() {
      this.isOpen = false;
    },
    sendNosto() {
      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewCart()
          .setPlacements(["cartpage-nosto-1", "cartpage-nosto-2"])
          .load()
          .then((data) => {
            console.log(data.recommendations["cartpage-nosto-1"]);
            console.log(data.recommendations["cartpage-nosto-2"]);
            this.nostoProductRecommendationsProduct =
              data.recommendations["cartpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["cartpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["cartpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["cartpage-nosto-2"]?.result_id || null;
          });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.description {
  font-size: 12px;
  font-weight: 300;
  margin-bottom: 20px;
  font-family: $heading-font-family;
}

.product {
  display: flex;
  font-size: 9px;
  font-weight: 600;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  &__image-wrapper {
    flex: 1 0 40%;
    margin-right: 20px;
  }

  &__image {
    max-width: 100%;
    height: auto;
  }

  &__details {
    flex: 1 0 calc(60% - 20px);
  }

  &__title {
    @include t__editorial-label-10;
    margin-bottom: 10px;
  }

  &__variant {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    @include t__utility-label-20;
  }

  &__price {
    margin-bottom: 12px;
    @include t__utility-label-20;
  }

  &__quantity {
    display: flex;
    justify-content: space-between;
  }
}

.cart-price {
  display: flex;
  justify-content: space-between;
  @include t__utility-label-30;
  margin-bottom: 20px;
}

.edit-variant-button,
.remove-product-button {
  outline: none;
  background: none;
  border: none;
  @include t__utility-body-10;
}

.button-group {
  ::v-deep(.button + .button) {
    margin-top: 10px;
  }
}

.estimate-delivery-date {
  padding: 0;
  margin-bottom: 20px;
}
</style>
